<template>
  <v-form v-model="isValidEvaluationForm">
    <v-row>
      <v-col>
        <v-autocomplete
          v-model="evaluation.idPersonalEvaluador"
          rounded
          filled
          dense
          color="secondary"
          background-color="primary"
          :items="evaluadores"
          item-value="idPersonal"
          item-text="CUIP"
          item-color="secondary"
          :rules="required"
          hint="Presione la tecla Enter para realizar la búsqueda"
          persistent-hint
          @keyup.enter="fetchEvaluadores($event.target.value)"
        >
          <template v-slot:label>Personal evaluador <span class="red--text">*</span> </template>
          <template v-slot:item="data">
            <v-list-item-avatar>
              <v-img v-if="data.item.foto !== ''" :src="data.item.foto"></v-img>
              <v-icon v-else>mdi-account</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ data.item.CUIP }}</v-list-item-title>
              <v-list-item-subtitle>{{ getFullName(data.item) }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col>
        <v-autocomplete
          v-model="evaluation.idMunicipio"
          rounded
          filled
          dense
          background-color="primary"
          color="secondary"
          :items="municipios"
          item-value="id"
          item-text="nombre"
          item-color="secondary"
          :rules="required"
        >
          <template v-slot:label>Municipio <span class="red--text">*</span> </template>
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.nombre }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.catEstado.title }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col>
        <v-menu
          v-model="isActiveMenuFechaInicioAcademico"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="fechaInicioFormatted"
              append-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              rounded
              filled
              dense
              color="secondary"
              background-color="primary"
              :rules="required"
            >
              <template v-slot:label>Fecha inicio <span class="red--text">*</span> </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="evaluation.fechaInicio"
            @input="isActiveMenuFechaInicioAcademico = false"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col>
        <v-menu
          v-model="menuFechaFinAcademico"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="fechaFinFormatted"
              append-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              rounded
              filled
              dense
              color="secondary"
              background-color="primary"
              :rules="required"
            >
              <template v-slot:label>Fecha de conclusión <span class="red--text">*</span> </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="evaluation.fechaFin"
            @input="menuFechaFinAcademico = false"
            :min="fechaInicioPermitida"
          ></v-date-picker>
        </v-menu>
      </v-col>
      <v-col>
        <v-menu
          v-model="menuFechaAplicacionAcademico"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="fechaAplicacionFormatted"
              append-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              rounded
              filled
              dense
              color="secondary"
              background-color="primary"
              :rules="required"
            >
              <template v-slot:label
                >Fecha de aplicación
                <span class="red--text">*</span>
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            v-model="evaluation.fechaAplicacion"
            :min="fechaFinalPermitida"
            @input="menuFechaAplicacionAcademico = false"
          ></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          v-model="evaluation.institucionSeguridadAspirada"
          rounded
          filled
          dense
          color="secondary"
          background-color="primary"
          :rules="required"
        >
          <template v-slot:label
            >Institución de seguridad pública a la que aplica
            <span class="red--text">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Aspirante al puesto de"
          v-model="evaluation.puestoAspirado"
          rounded
          filled
          dense
          color="secondary"
          background-color="primary"
          :rules="required"
        >
          <template v-slot:label>Aspirante al puesto de <span class="red--text">*</span> </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" align="center">
        <v-btn
          color="secondary"
          @click="initializeAcademicPerformanceEvaluations()"
          :disabled="loading || !isValidEvaluationForm"
          >Guardar información</v-btn
        >
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import { HUMAN_READABLE_DATE_FORMAT, VUETIFY_DATE_FORMAT } from '../../../../const'
import DateAdapter from '../../../../utils/DateAdapter'
import { ACADEMIC_PERFORMANCE_EVALUATION_TYPES } from '../../../../modules/evaluacion-desempeño-academico/domain/entities/Evaluation'
import CreateRespetoValoresPrincipios from '@/modules/respeto-valores-principios/application/CreateRespetoValoresPrincipios.usecase'
import RespetoValoresPrincipiosApiRepository from '@/modules/respeto-valores-principios/infrastructure/RespetoValoresPrincipiosApi.repository'
import CreateRendimientoProfesionalismo from '@/modules/rendimiento-profesionalismo/application/CreateRendimientoProfesionalismo.usecase'
import RendimientoProfesionalismoApiRepository from '@/modules/rendimiento-profesionalismo/infrastructure/RendimientoProfesionalismoApi.repository'
import CreateDesempeñoInstitucional from '@/modules/desempeño-institucional/application/CreateDesempeñoInstitucional.usecase'
import DesempeñoInstitucionalApiRepository from '@/modules/desempeño-institucional/infrastructure/DesempeñoInstitucionalApi.repository'
import Notificacion from '@/modules/notificaciones/domain/Notificacion'
import { NotificationFactory, NOTIFICATION_TYPES } from '@/modules/notificaciones/domain/NotificationFactory'

export default {
  props: {
    idPersonal: {
      type: Number,
      required: true,
    },

    evaluation: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      isValidEvaluationForm: false,
      isActiveMenuFechaInicioAcademico: false,
      menuFechaFinAcademico: false,
      menuFechaAplicacionAcademico: false,
      required: [(v) => !!v || 'Este campo es requerido'],
    }
  },

  watch: {
    evaluation: {
      handler(newValue) {
        if (newValue.fechaInicio) {
          this.setFechaFin()
        }
      },

      deep: true,
    },
  },

  computed: {
    ...mapGetters('personal', { evaluadores: 'getEvaluadores' }),
    ...mapGetters('municipio', { municipios: 'getMunicipios' }),
    ...mapGetters('personal', { personalEvaluado: 'getPersonalSingle' }),

    fechaInicioFormatted() {
      return this.evaluation.fechaInicio
        ? new DateAdapter(this.evaluation.fechaInicio, VUETIFY_DATE_FORMAT).toHumanReadable()
        : ''
    },

    fechaFinFormatted() {
      return this.evaluation.fechaFin
        ? new DateAdapter(this.evaluation.fechaFin, VUETIFY_DATE_FORMAT).toHumanReadable()
        : ''
    },

    fechaAplicacionFormatted() {
      return this.evaluation.fechaAplicacion
        ? new DateAdapter(this.evaluation.fechaAplicacion, VUETIFY_DATE_FORMAT).toHumanReadable()
        : ''
    },

    fechaInicioPermitida() {
      return this.fechaInicioFormatted != ''
        ? new DateAdapter(this.fechaInicioFormatted, HUMAN_READABLE_DATE_FORMAT).toUiSupportedFormat()
        : ''
    },

    fechaFinalPermitida() {
      return this.evaluation.fechaFin
        ? moment(this.evaluation.fechaFin, VUETIFY_DATE_FORMAT).add(1, 'day').format(VUETIFY_DATE_FORMAT)
        : ''
    },
  },

  methods: {
    ...mapActions('personal', ['fetchEvaluadores']),
    ...mapActions('notificaciones', ['createNotification']),
    async sendNotificacion() {
      const evaluado = this.personalEvaluado || {}
      const nombreEvaluado = this.getFullName(evaluado) || 'Sin evaluado'
      const cuipEvaluado = evaluado.CUIP || 'Sin CUIP'

      const nombreEvaluador = [
        localStorage.getItem('nombre'),
        localStorage.getItem('segundoNombre'),
        localStorage.getItem('apaterno'),
        localStorage.getItem('amaterno'),
      ]
        .filter(Boolean)
        .join(' ')

      const mensaje = `El personal ${nombreEvaluador} ha registrado un proceso de evaluación de desempeño al elemento ${nombreEvaluado} con CUIP: ${cuipEvaluado}.`

      const notification = new NotificationFactory(Notificacion).createNotification(
        NOTIFICATION_TYPES.CAMBIO_EVALUACION_DESEMPENO_ACADEMICO,
        {
          responsable: Number(localStorage.getItem('idPersonal')),
          afectado: Number(this.idPersonal),
          mensaje,
        }
      )

      await this.createNotification(notification)
    },

    getFullName(persona) {
      if (!persona) return ''
      return [persona.nombre, persona.segundoNombre, persona.apaterno, persona.amaterno].filter(Boolean).join(' ')
    },

    async initializeAcademicPerformanceEvaluations() {
      this.loading = true

      try {
        const createRespectForValuesAndPrinciplesPromise = new CreateRespetoValoresPrincipios(
          new RespetoValoresPrincipiosApiRepository()
        ).execute(this.evaluation, this.idPersonal)

        const createPerformanceAndProffesionalismPromise = new CreateRendimientoProfesionalismo(
          new RendimientoProfesionalismoApiRepository()
        ).execute(this.evaluation, this.idPersonal)

        const createInstitutionalPerformancePromise = new CreateDesempeñoInstitucional(
          new DesempeñoInstitucionalApiRepository()
        ).execute(this.evaluation, this.idPersonal)

        const [
          respectForValuesAndPrinciplesEvaluationId,
          performanceAndProfessionalismEvaluationId,
          institutionalPerformanceEvaluationId,
        ] = await Promise.all([
          createRespectForValuesAndPrinciplesPromise,
          createPerformanceAndProffesionalismPromise,
          createInstitutionalPerformancePromise,
        ])
        await this.sendNotificacion()

        this.$emit('save', {
          respectForValuesAndPrinciplesEvaluationId,
          performanceAndProfessionalismEvaluationId,
          institutionalPerformanceEvaluationId,
        })
      } catch (error) {
        console.error('Error initializing evaluations:', error)
      }

      this.loading = false
    },

    setFechaFin() {
      if (
        moment(this.evaluation.fechaInicio, VUETIFY_DATE_FORMAT).isAfter(
          moment(this.evaluation.fechaFin, VUETIFY_DATE_FORMAT)
        )
      ) {
        this.evaluation.fechaFin = ''
      }
    },
  },
}
</script>
